import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, BACKEND_URL, describeApiError } from "@/lib/api";
import { toast } from "sonner";
import { useWallet } from "@/contexts/WalletContext";
import usePromo from "@/hooks/usePromo";
import usePaymentsGate from "@/hooks/usePaymentsGate";

const isAiUnavailable = (e) => {
  const s = e.response?.status;
  const d = e.response?.data?.detail || "";
  return s === 503 || /not configured|access_denied|FREE_USER/i.test(d);
};

/** All state + handlers for the /create wizard — keeps Create.jsx a thin renderer. */
export default function useCreateFlow() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const resumeSlug = params.get("resume");
  const { primaryAddress } = useWallet();
  const [step, setStep] = useState(0);
  const [resuming, setResuming] = useState(!!resumeSlug);
  const [form, setForm] = useState({
    name: "",
    ticker: "",
    supply: 1_000_000_000,
    theme: "meme",
    description: "",
  });
  const [coin, setCoin] = useState(null); // server-side coin doc after step 1
  const [logoUrl, setLogoUrl] = useState(null);
  const [whitepaper, setWhitepaper] = useState(null);
  const [wpTone, setWpTone] = useState("playful"); // playful | serious | degen
  const [logoBusy, setLogoBusy] = useState(false);
  const [wpBusy, setWpBusy] = useState(false);
  const [aiError, setAiError] = useState(""); // surfaces a clear inline message when AI is unavailable
  const [submitBusy, setSubmitBusy] = useState(false);
  const [publishBusy, setPublishBusy] = useState(false);

  const promoProps = usePromo("PUBLISH");
  const payments = usePaymentsGate(step === 3); // probe Stripe only on the publish step

  const next = () => setStep((s) => Math.min(s + 1, 3));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  // ---- /create?resume=<slug>: restore a saved draft at the first unfinished step ----
  useEffect(() => {
    if (!resumeSlug) return;
    let cancelled = false;
    api
      .get(`/coins/${resumeSlug}`)
      .then(({ data }) => {
        if (cancelled) return;
        if (data.status !== "draft") {
          navigate(`/c/${data.slug}`, { replace: true });
          return;
        }
        setForm({
          name: data.name || "",
          ticker: data.ticker || "",
          supply: data.supply || 1_000_000_000,
          theme: data.theme || "meme",
          description: data.description || "",
        });
        setCoin(data);
        if (data.logo_b64) setLogoUrl(`${BACKEND_URL}/api/coins/${data.slug}/logo`);
        if (data.whitepaper) setWhitepaper(data.whitepaper);
        if (data.whitepaper_tone) setWpTone(data.whitepaper_tone);
        setStep(data.whitepaper ? 3 : data.logo_b64 ? 2 : 1);
        toast.success(`Draft restored — $${data.ticker}`, { description: "Picking up where you left off." });
      })
      .catch(() => {
        if (!cancelled) toast.error("Couldn't load that draft — starting fresh.");
      })
      .finally(() => {
        if (!cancelled) setResuming(false);
      });
    return () => {
      cancelled = true;
    };
  }, [resumeSlug, navigate]);

  // ---- Step 0 → 1: create draft on server ----
  const submitDraft = async () => {
    if (!form.name || !form.ticker) {
      toast.error("Name and ticker required");
      return;
    }
    const cleanTicker = form.ticker.trim().toUpperCase().replace(/[^A-Z0-9]/g, "");
    const cleanSupply = Number(String(form.supply).replace(/[^0-9]/g, ""));
    if (cleanTicker.length < 2 || cleanTicker.length > 8) {
      toast.error("Ticker must be 2–8 letters/numbers — no $, spaces or symbols");
      return;
    }
    if (form.name.trim().length < 2) {
      toast.error("Name needs at least 2 characters");
      return;
    }
    if (!cleanSupply || cleanSupply <= 0) {
      toast.error("Supply must be a number above 0 (e.g. 1000000000)");
      return;
    }
    setSubmitBusy(true);
    try {
      const { data } = await api.post("/coins", {
        name: form.name.trim(),
        ticker: cleanTicker,
        supply: cleanSupply,
        theme: form.theme,
        description: form.description.trim() || null,
        creator_wallet: primaryAddress || null,
      });
      setCoin(data);
      next();
    } catch (e) {
      toast.error(describeApiError(e, "Couldn't create draft — please try again."), { duration: 8000 });
    } finally {
      setSubmitBusy(false);
    }
  };

  const uploadLogo = async (file) => {
    if (!coin || !file) return;
    if (!/^image\/(png|jpeg|jpg|webp|gif)$/.test(file.type)) {
      toast.error("Use PNG, JPG, WEBP, or GIF");
      return;
    }
    if (file.size > 3_000_000) {
      toast.error("Image too large — max 3 MB");
      return;
    }
    setLogoBusy(true);
    setAiError("");
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = () => reject(new Error("file read failed"));
        r.readAsDataURL(file);
      });
      await api.post(`/coins/${coin.slug}/logo/upload`, { data_url: dataUrl });
      setLogoUrl(`${BACKEND_URL}/api/coins/${coin.slug}/logo?ts=${Date.now()}`);
      toast.success("Logo uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setLogoBusy(false);
    }
  };

  const genLogo = async () => {
    if (!coin) return;
    setLogoBusy(true);
    setAiError("");
    try {
      await api.post(`/coins/${coin.slug}/ai/logo`);
      // bust cache by timestamp
      setLogoUrl(`${BACKEND_URL}/api/coins/${coin.slug}/logo?ts=${Date.now()}`);
      toast.success("Logo generated");
    } catch (e) {
      if (isAiUnavailable(e)) {
        setAiError("AI logo isn't configured yet. You can skip this step — your coin will publish without a custom logo (we'll show your ticker instead).");
      } else {
        toast.error(describeApiError(e, "Logo failed — try again."), { duration: 8000 });
      }
    } finally {
      setLogoBusy(false);
    }
  };

  const genWhitepaper = async ({ force = false } = {}) => {
    if (!coin) return;
    setWpBusy(true);
    setAiError("");
    try {
      const { data } = await api.post(`/coins/${coin.slug}/ai/whitepaper`, { tone: wpTone, force });
      setWhitepaper(data.whitepaper);
      toast.success(force ? `Whitepaper rewritten — ${wpTone} tone` : "Whitepaper drafted");
    } catch (e) {
      if (isAiUnavailable(e)) {
        setAiError("AI whitepaper isn't configured. Skip this step — you can edit your coin's details anytime after publishing.");
      } else {
        toast.error(e.response?.data?.detail || "Whitepaper failed — try again");
      }
    } finally {
      setWpBusy(false);
    }
  };

  const publish = async (sku = "PUBLISH") => {
    if (!coin) return;
    setPublishBusy(sku);
    try {
      const { data } = await api.post("/payments/checkout", {
        sku,
        coin_slug: coin.slug,
        origin_url: window.location.origin,
        promo_code: sku === "PUBLISH" ? promoProps.promo?.code || null : null,
      });
      window.location.href = data.url; // Stripe redirect
    } catch (e) {
      setPublishBusy(false);
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || "";
      if (status === 503 || detail.includes("stripe_not_configured")) {
        toast.error("Payments are temporarily offline. Your draft is saved — try again in a minute.");
        payments.markPaymentsDown("stripe_not_configured");
      } else if (status === 502 || detail.includes("stripe_create_session_failed")) {
        toast.error("Stripe hit a snag. Your draft is saved — try again or email founder@solspur.com.");
        payments.markPaymentsDown("stripe_api_rejected");
      } else {
        toast.error(detail || "Payment failed — your draft is saved, try again");
      }
    }
  };

  const previewOnly = () => {
    if (!coin) return;
    navigate(`/c/${coin.slug}`);
  };

  return {
    step, next, back,
    form, setForm,
    coin, logoUrl, whitepaper, wpTone, setWpTone,
    logoBusy, wpBusy, aiError, submitBusy, publishBusy,
    promoProps, payments, primaryAddress, resuming,
    submitDraft, uploadLogo, genLogo, genWhitepaper, publish, previewOnly,
  };
}
